import { useEffect, useState } from 'react';

export function getSlideImageUrl(slide: SpApi.ISlideshowSlide): string | null {
    const firstItem = slide.items.find((item) => item.displayUrl !== null);

    return (
        firstItem?.displayUrl?.extraLarge3 ??
        firstItem?.displayUrl?.extraLarge2 ??
        firstItem?.displayUrl?.large ??
        firstItem?.displayUrl?.medium ??
        null
    );
}

/**
 * Preloads every slide's image into the browser cache ahead of playback, so a
 * slide's asset is already available by the time its transition begins — without
 * this, the incoming slide's <img> only starts fetching once it enters the DOM
 * (i.e. when the transition itself starts), which pops in mid-animation on a cold
 * cache (typically the first playthrough after a hard refresh).
 *
 * A slide with no image, or one whose image fails to load, is still marked ready
 * so a single broken asset can't stall playback indefinitely.
 */
export function useSlideshowImagePreload(slides: SpApi.ISlideshowSlide[]): ReadonlySet<number> {
    const [readyIndices, setReadyIndices] = useState<ReadonlySet<number>>(new Set());

    useEffect(() => {
        setReadyIndices(new Set());

        let cancelled = false;
        const images: HTMLImageElement[] = [];

        const markReady = (index: number) => {
            if (cancelled) {
                return;
            }

            setReadyIndices((prev) => {
                if (prev.has(index)) {
                    return prev;
                }

                const next = new Set(prev);
                next.add(index);
                return next;
            });
        };

        slides.forEach((slide, index) => {
            const url = getSlideImageUrl(slide);

            if (!url) {
                markReady(index);
                return;
            }

            const img = new Image();

            // Fetching alone leaves the bitmap undecoded, so the browser pays the JPEG decode on
            // the main thread the first time the slide's <img> actually paints — one frame into
            // the transition, which reads as a pause part-way through the crossfade. Safari is
            // where this hurt most (FOR-16442). `decode()` does that work up front instead.
            // Treat a rejection as ready anyway: same policy as a load failure, since a slide
            // that can't be decoded must not stall playback forever.
            const markReadyOncePaintable = () => {
                if (typeof img.decode !== 'function') {
                    markReady(index);

                    return;
                }

                img.decode().then(
                    () => markReady(index),
                    () => markReady(index)
                );
            };

            images.push(img);
            img.onload = markReadyOncePaintable;
            img.onerror = () => markReady(index);
            img.src = url;

            if (img.complete) {
                markReadyOncePaintable();
            }
        });

        return () => {
            cancelled = true;
            images.forEach((img) => {
                img.onload = null;
                img.onerror = null;
            });
        };
    }, [slides]);

    return readyIndices;
}
